import { useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Maximize2 } from "lucide-react";

interface ImageCarouselProps {
  images: string[];
  title: string;
}

const ImageCarousel = ({ images, title }: ImageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  return (
    <Card className="border-l-2 border-t-2 border-primary/30">
      <CardContent className="p-4 relative">
        <Carousel
          setApi={(api) => {
            api?.on("select", () => {
              setCurrentIndex(api.selectedScrollSnap());
            });
          }}
        >
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={index}>
                <div className="relative">
                  <img
                    src={image}
                    alt={`${title} - Image ${index + 1}`}
                    className="w-full h-auto rounded-md object-cover aspect-video"
                  />
                  <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="absolute bottom-2 right-2 bg-background/80 backdrop-blur-sm"
                        onClick={() => setIsFullscreen(true)}
                      >
                        <Maximize2 className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl w-full p-1">
                      <img
                        src={image}
                        alt={`${title} - Image ${index + 1}`}
                        className="w-full h-auto"
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-2" />
          <CarouselNext className="right-2" />
        </Carousel>
        
        <div className="mt-2 flex justify-between items-center">
          <p className="text-sm text-muted-foreground">
            Image {currentIndex + 1} of {images.length}
          </p>
        </div>
        
        {/* Thumbnail navigation */}
        <div className="mt-3 flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => {
                const carousel = document.querySelector(".carousel-viewport");
                if (carousel) {
                  carousel.scrollTo({
                    left: carousel.clientWidth * index,
                    behavior: "smooth",
                  });
                }
                setCurrentIndex(index);
              }}
              className={`flex-shrink-0 border-2 rounded overflow-hidden transition-all w-16 h-12 ${
                currentIndex === index
                  ? "border-primary"
                  : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ImageCarousel;